/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  withSitecoreContext,
  Placeholder,
} from '@sitecore-jss/sitecore-jss-react';
import ReactDOM from 'react-dom';
import { withNamespaces } from 'react-i18next';
import GoogleMapReact from 'google-map-react';
import PropTypes from 'prop-types';
import compose from 'lodash/fp/compose';
import some from 'lodash/some';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import take from 'lodash/take';
import find from 'lodash/find';
import truncate from 'lodash/truncate';
import toNumber from 'lodash/toNumber';
import isEmpty from 'lodash/isEmpty';
import Spacer from '../../ui/Spacer';
import DotComponent from '../../ui/DotComponent';
import { Icon } from '../../ui/Icon';
import CtaLink from '../../ui/CtaLink';
import { ZoomInUIControl, ZoomOutUIControl } from '../../ui/Map';
import { GOOGLE_MAPS_KEY, TRANSPORTATION } from '../../../utils/constants';
import { MapAndContact } from '../../../utils/dictionarykeys';
import { getGoogleMapLocation } from '../../../utils/googleMaps';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import Schedule from './schedule';
import { Container, Row, Column } from '../../../styles/Grid';
import { mapStyles } from './mapStyles';
import applyPluralRule from '../../../utils/pluralRule';
import {
  mapContainerCss,
  mapBlockCss,
  headingCss,
  addressCss,
  contentCss,
  contentBlockCss,
  dataTitleCss,
  contentWrapperCss,
  dataContentTitleCss,
  open247IconCss,
  gettingThereTypeCss,
  dataContentValueCss,
  containerMarginCss,
  nearPlaceMapRenderCss,
  locationPlaceCss,
  phoneNumberCss,
  nearByLocationsCss,
  otherLocationsTextCss,
  otherLocationsLinkCss,
  squaredotCSs,
} from './index.styles';

const DEFAULT_MAP_ZOOM = 16;
const NEAREST_PLACE_KEYWORD = 'transit station';
const defaultData = {
  Address1: {
    value: '',
  },
  City: { value: '' },
  State: { value: '' },
  ZipCode: { value: '' },
  Phone: { value: '' },
  GetThereOptions: {
    ClosestAirport: { value: '' },
    ClosestStation: { value: '' },
  },
  __NearbyLocations: { value: [] },
  Latitude: { value: '0' },
  Longitude: { value: '0' },
  OpeningHours: {
    Schedule: [],
  },
  NumberOfNearbyLocations: 0,
  ParentNameLocation: '',
  LocationsAroundCTA: {
    value: '',
  },
  MeasuringSystem: {
    value: 0, // IMPERIAL: 1, METRIC: 0
  },
  GoogleMapsSearchRadius: {
    value: 48280, // 30 miles
  },
};

const NearPlaceMapRender = ({ name }) => {
  return <div css={nearPlaceMapRenderCss}>{name}</div>;
};

const LocationPlaceMapRender = ({ name }) => {
  return <div css={locationPlaceCss}>{name.value}</div>;
};

const mapOptions = () => {
  return {
    styles: mapStyles,
    zoomControl: false,
    fullscreenControl: false,
  };
};

const nearByLocationsFormat = number => {
  const formatNumber = toNumber(number);

  return formatNumber > 99 ? '99+' : formatNumber;
};

const renderNearByLocations = number => {
  return <p css={nearByLocationsCss}>{nearByLocationsFormat(number)}</p>;
};

const hasLocations = number => {
  const formatNumber = toNumber(number);
  return !!(formatNumber && formatNumber > 0);
};

const getStationIcon = type => {
  return <i className={`icon-${type}`} />;
};

export class ContactAndMap extends SafePureComponent {
  state = {
    fields: defaultsDeep(this.props.fields, defaultData),
    isEditing: get(this.props, 'sitecoreContext.pageEditing', false),
    map: null,
    nearestStations: [],
    pageLanguage: get(this.props, 'sitecoreContext.language', 'en-US'),
    zoomInControl: null,
    zoomOutControl: null,
  };

  handleZoomOut = () => {
    const { map } = this.state;

    map && map.setZoom(map.getZoom() - 1);
  };

  handleZoomIn = () => {
    const { map } = this.state;

    map && map.setZoom(map.getZoom() + 1);
  };

  componentDidMount() {
    if (typeof document !== 'undefined') {
      const zoomInControl = document.createElement('div');
      const zoomOutControl = document.createElement('div');

      zoomInControl.className = 'zoom-in-control';
      zoomOutControl.className = 'zoom-out-control';

      this.setState({ zoomInControl, zoomOutControl });
    }
  }

  componentWillUnmount() {
    const { zoomInControl, zoomOutControl } = this.state;
    if (window.google) {
      window.google.maps.event.removeListener(
        zoomOutControl,
        'click',
        this.handleZoomOut,
      );
      window.google.maps.event.removeListener(
        zoomInControl,
        'click',
        this.handleZoomIn,
      );
    }
  }

  /**
   * getNearestStations: Maps the values from the nearest stations and its distances.
   * @param {Array} nearByStations Array with the 2 closest stations to the office.
   * @param {Array} distances Array with the distance results from the office location, (google discance service API).
   * @returns {Object} Object with the schema needed in the state.
   */
  getNearestStations = (nearByStations, distances) =>
    nearByStations.map((station, index) => {
      const stationType = get(station, 'types[0]');

      // Bus stations are not considered by product, so they need to be all replaced to transit stations.
      const getStationType =
        stationType === TRANSPORTATION.TYPES.BUS
          ? TRANSPORTATION.TYPES.TRANSIT
          : stationType;

      return {
        name: get(station, 'name'),
        type: getStationType,
        distance: {
          text: get(distances, `rows[0].elements[${index}].distance.text`),
          value: get(distances, `rows[0].elements[${index}].distance.value`),
        },
      };
    });

  /**
   * handleNearestStations: handles all functionality related to "getting there" section.
   * @param {Object} map Map instance.
   * @param {Object} googleMaps Google maps library instance.
   * @param {Number} lat Current location Latitude number.
   * @param {Number} lng Current location Longitude number.
   */
  handleNearestStations(map, googleMaps, lat, lng) {
    const officeLocation = new googleMaps.LatLng({ lat, lng });

    // Docs: https://developers.google.com/maps/documentation/javascript/distancematrix
    const distanceService = new googleMaps.DistanceMatrixService();

    // Docs: https://developers.google.com/maps/documentation/javascript/reference/places-service
    const placesService = new googleMaps.places.PlacesService(map);

    // This request payload does all the magic, it gets the closest stations.
    const placesRequest = {
      keyword: NEAREST_PLACE_KEYWORD,
      location: officeLocation,
      rankBy: googleMaps.places.RankBy.DISTANCE,
    };

    placesService &&
      placesService.nearbySearch(placesRequest, (stations, status) => {
        if (status !== 'OK') return;

        const nearestStation = take(stations, 1);

        const nearestStationType =
          !isEmpty(nearestStation) && get(nearestStation[0], 'types[0]');
        const secondNearestStation = find(stations, station => {
          const stationMainType = get(station, 'types[0]');

          return (
            stationMainType !== nearestStationType &&
            stationMainType !== TRANSPORTATION.TYPES.POINT &&
            stationMainType !== TRANSPORTATION.TYPES.BUS
          );
        });

        let nearByStations = [];

        !isEmpty(nearestStation) && nearByStations.push(nearestStation[0]);

        Boolean(secondNearestStation) &&
          nearByStations.push(secondNearestStation);

        if (!isEmpty(nearByStations)) {
          const locationOfNearByStations = () =>
            nearByStations.map(station => station.geometry.location);

          const distanceRequest = {
            origins: [officeLocation],
            destinations: locationOfNearByStations(),
            travelMode: 'WALKING',
            unitSystem: this.state.fields.MeasuringSystem.value,
          };

          distanceService.getDistanceMatrix(
            distanceRequest,
            (distances, status) => {
              if (status === 'OK') {
                this.setState({
                  nearestStations: this.getNearestStations(
                    nearByStations,
                    distances,
                  ),
                });
              }
            },
          );
        }
      });
  }

  /**
   * handleMapControls: Sets map controls.
   * @param {Object} map Map instance.
   * @param {Object} googleMaps Google maps library instance.
   */
  handleMapControls(map, googleMaps) {
    const leftSideMap = map.controls[googleMaps.ControlPosition.LEFT_TOP];
    const { zoomInControl, zoomOutControl } = this.state;

    ReactDOM.render(<ZoomInUIControl />, zoomInControl);
    ReactDOM.render(<ZoomOutUIControl />, zoomOutControl);

    leftSideMap.push(zoomInControl);
    leftSideMap.push(zoomOutControl);

    googleMaps.event.addDomListener(zoomInControl, 'click', this.handleZoomIn);

    googleMaps.event.addDomListener(
      zoomOutControl,
      'click',
      this.handleZoomOut,
    );
  }

  /**
   * handleMapOnLoad: Similar to "ComponentDidMount" for google maps, it handles all related map functionality.
   * @param {Object} map Map instance.
   * @param {Object} googleMaps Google maps library instance.
   * @param {Number} lat Current location Latitude number.
   * @param {Number} lng Current location Longitude number.
   */
  handleMapOnLoad(map, googleMaps, lat, lng) {
    this.setState({ map: map });
    this.handleNearestStations(map, googleMaps, lat, lng);
    this.handleMapControls(map, googleMaps);
  }

  /**
   * isStationNearBy: Compares the distance setted at the cms with the current station.
   * @param {Object} map Map instance.
   * @param {Object} googleMaps Google maps library instance.
   * @param {Number} lat Latitude number.
   * @param {Number} lng Longitude number.
   */
  isStationNearBy(stationDistance) {
    return stationDistance < this.state.fields.GoogleMapsSearchRadius.value;
  }

  renderNearestStations(stations) {
    const { t } = this.props;

    return stations
      .filter(({ distance }) => this.isStationNearBy(distance.value))
      .map((station, index) => {
        const { distance, type, name } = station;

        return (
          <li key={index}>
            {getStationIcon(type)}
            <span css={[dataContentTitleCss, gettingThereTypeCss]}>
              {t(
                TRANSPORTATION.DICTIONARY_KEYS[type] ||
                  TRANSPORTATION.DICTIONARY_KEYS.transit_station,
              )}
            </span>

            <span css={dataContentValueCss}>
              {truncate(name, { length: 23 })}
              <span css={squaredotCSs}>.</span>
              {distance.text}
            </span>
          </li>
        );
      });
  }

  renderNearByMapLocations() {
    const { __NearbyLocations } = this.state.fields;

    return __NearbyLocations.value
      .filter(place => {
        const lat = parseFloat(place.Latitude);
        const lng = parseFloat(place.Longitude);

        return !isNaN(lat) && !isNaN(lng);
      })
      .value.map((place, index) => {
        const lat = parseFloat(place.Latitude);
        const lng = parseFloat(place.Longitude);

        return (
          <NearPlaceMapRender
            key={index}
            lat={lat}
            lng={lng}
            name={place.Name}
          />
        );
      });
  }

  getMapLanguage() {
    return this.state.pageLanguage.split('-')[0];
  }

  renderMap(lat, lng) {
    const { Address1 } = this.state.fields;

    return (
      <GoogleMapReact
        yesIWantToUseGoogleMapApiInternals
        options={mapOptions}
        bootstrapURLKeys={{
          key: GOOGLE_MAPS_KEY,
          language: this.getMapLanguage(),
          libraries: ['places', 'geometry', 'distance matrix', 'geocoder'],
        }}
        defaultCenter={{ lat, lng }}
        defaultZoom={DEFAULT_MAP_ZOOM}
        onGoogleApiLoaded={({ map, maps }) =>
          this.handleMapOnLoad(map, maps, lat, lng)
        }
      >
        {this.renderNearByMapLocations}
        <LocationPlaceMapRender lat={lat} lng={lng} name={Address1} />
      </GoogleMapReact>
    );
  }

  safeRender() {
    const { props, state } = this;
    const { t } = props;
    const { nearestStations, fields, isEditing } = state;
    const {
      Address1,
      City,
      Latitude,
      LocationsAroundCTA,
      Longitude,
      NumberOfNearbyLocations,
      OpeningHours,
      ParentNameLocation,
      ShowReceptionHours,
      State,
      ZipCode,
    } = fields;
    const ShowReceptionHoursValue = get(ShowReceptionHours, 'value', false);
    const lat = parseFloat(Latitude.value);
    const lng = parseFloat(Longitude.value);
    const componentName = get(props, 'rendering.componentName', '');
    const receptionHours = get(OpeningHours, 'value', {});

    // If any of the stations found is out of range the component should not be rendered.
    const shouldNearestStationsRender = () => {
      if (!isEmpty(nearestStations)) {
        return some(nearestStations, station =>
          this.isStationNearBy(station.distance.value),
        );
      }
    };

    const otherLocationText = applyPluralRule({
      quantity: NumberOfNearbyLocations,
      dictionary: 'OtherLocationsAround',
    });

    return (
      <Spacer
        marginCss={containerMarginCss}
        containerProps={{ 'data-sticky-id': get(props, 'rendering.uid') }}
      >
        <Container>
          <Row>
            <Column
              cols={{ sm: 6, md: 8, lg: 8 }}
              offsets={{ sm: 0, md: 0, lg: 4 }}
            >
              <h2 css={headingCss}>
                <span>{t(MapAndContact.WhereTitle)}</span>
                <DotComponent />
              </h2>

              <div>
                <p
                  css={addressCss}
                >{`${Address1.value}, ${City.value}, ${State.value} ${ZipCode.value}`}</p>
                <CtaLink
                  target="_blank"
                  href={getGoogleMapLocation(lat, lng)}
                  text={t(MapAndContact.GetDirections)}
                  componentName={componentName}
                  isGTMTrackable
                />
              </div>

              {(isEditing || hasLocations(NumberOfNearbyLocations)) && (
                <a href={LocationsAroundCTA.value} css={otherLocationsLinkCss}>
                  {renderNearByLocations(NumberOfNearbyLocations)}
                  <span css={otherLocationsTextCss}>
                    {otherLocationText}
                    {` ${ParentNameLocation}`}
                  </span>
                </a>
              )}
            </Column>
          </Row>
          <Row>
            <Column
              css={mapContainerCss}
              cols={{ sm: 6, md: 8, lg: 8 }}
              offsets={{ sm: 0, md: 0, lg: 0 }}
            >
              {lat && lng && (
                <div css={[mapBlockCss, { height: '100vh', width: '100%' }]}>
                  {this.renderMap(lat, lng)}
                </div>
              )}
            </Column>

            <Column css={contentBlockCss} cols={{ sm: 6, md: 8, lg: 4 }}>
              <div css={contentCss}>
                {shouldNearestStationsRender() && (
                  <div css={contentWrapperCss}>
                    <h3 css={dataTitleCss}>{t(MapAndContact.GettingThere)}</h3>
                    <ul>{this.renderNearestStations(nearestStations)}</ul>
                  </div>
                )}

                <div css={phoneNumberCss}>
                  <Placeholder
                    name="jss-phone"
                    rendering={props.rendering}
                    params={{ isSecondaryType: true, componentName }}
                  />
                </div>

                <div css={contentWrapperCss}>
                  <h3 css={dataTitleCss}>
                    {t(MapAndContact.twentyFourAccessTitle)}
                  </h3>
                  <span css={dataContentTitleCss}>
                    <Icon name="open-247" customCss={open247IconCss} />
                    {t(MapAndContact.twentyFourAccessText)}
                  </span>
                </div>
              </div>

              {ShowReceptionHoursValue && receptionHours && (
                <div css={contentCss}>
                  <div css={contentWrapperCss}>
                    <h3 css={dataTitleCss}>
                      {t(MapAndContact.ReceptionHours)}
                    </h3>
                    <ul>
                      {Object.keys(receptionHours).map(label => (
                        <Schedule
                          key={label}
                          day={t(label)}
                          value={receptionHours[label]}
                        />
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

ContactAndMap.displayName = 'ContactAndMap';

ContactAndMap.defaultProps = {
  fields: defaultData,
  t: key => key,
};

const objectValueType = {
  value: PropTypes.string,
};

ContactAndMap.propTypes = {
  Address1: PropTypes.shape(objectValueType),
  City: PropTypes.shape(objectValueType),
  ClosestAirport: PropTypes.shape(objectValueType),
  ClosestStation: PropTypes.shape(objectValueType),
  Latitude: PropTypes.shape(objectValueType),
  Longitude: PropTypes.shape(objectValueType),
  GoogleMapsSearchRadius: PropTypes.shape(objectValueType),
  Phone: PropTypes.shape(objectValueType),
  State: PropTypes.shape(objectValueType),
  OpeningHours: PropTypes.shape({
    Schedule: PropTypes.arrayOf(objectValueType),
  }),
  MeasuringSystem: PropTypes.shape(objectValueType),
  ZipCpde: PropTypes.shape(objectValueType),
};

export default compose(withSitecoreContext(), withNamespaces())(ContactAndMap);
